import React from "react";
import ContactUs from "../../../components/ContactUs";
import { Helmet } from "react-helmet-async";

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const SectionHeading = ({ title, subtitle }) => (
  <div className="text-center mb-8">
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
      {title}
    </h2>
    {subtitle && (
      <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-4xl mx-auto">
        {subtitle}
      </p>
    )}
  </div>
);

const FeatureCard = ({ feature }) => (
  <div
    className="relative flex flex-col space-y-4 rounded-2xl p-6 shadow-md shadow-[0_4px_6px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.05),0_0_15px_4px_rgba(16,185,129,0.25)] 
     rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:border-none hover:shadow-none"
    // style={{
    //   boxShadow:
    //     "0 4px 6px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05), 0 0 15px 4px rgba(16,185,129,0.25)",
    // }}
  >
    <div className="flex items-center space-x-4">
      <div className="h-12 w-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-2xl">
        {feature.icon}
      </div>
      <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
    </div>
    <p className="text-lg text-gray-700">{feature.description}</p>
  </div>
);

const StepCard = ({ step, index }) => (
  <div className="flex flex-col sm:flex-row  sm:items-start gap-4">
    {/* <div className="flex-shrink-0">
      <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xl font-bold">
        {index + 1}
      </div>
    </div> */}
    <div className=" rounded-2xl p-5 shadow-md shadow-[0_4px_6px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.05),0_0_15px_4px_rgba(16,185,129,0.25)] 
      rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:border-none  flex-1">
      <h4 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h4>
      <p className="text-gray-700 text-lg">{step.description}</p>
    </div>
  </div>
);

const UseCaseCard = ({ useCase }) => (
  <div
    className="relative flex flex-col space-y-3 rounded-2xl p-6   rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:border-none hover:shadow-none"
    // style={{
    //   boxShadow:
    //     "0 4px 6px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05), 0 0 15px 4px rgba(59,130,246,0.25)",
    // }}
  >
    <p className="text-sm uppercase tracking-wider text-blue-500 font-semibold">
      {useCase.industry}
    </p>
    <h3 className="text-xl font-semibold text-gray-900">{useCase.title}</h3>
    <p className="text-gray-700 text-lg">{useCase.description}</p>
  </div>
);

const AIAutomationTemplate = ({
  title,
  heroDescription,
  about,
  features,
  steps,
  benefits,
  expertise,
  image,
  useCases,
  reasons,
  ctaLabel = "Schedule a Strategy Call",
  meta = {},
}) => {
  return (
    <>
      <Helmet>
        <title>{meta.title || `${title} | Ayanshtech Solutions`}</title>
        {meta.description && (
          <meta name="description" content={meta.description} />
        )}
        <meta property="og:title" content={meta.ogTitle || title} />
        {meta.ogDescription && (
          <meta property="og:description" content={meta.ogDescription} />
        )}
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content={meta.ogImage || "http://ayanshtechnology.com/assets/logo.png"}
        />
        <meta
          property="og:url"
          content={meta.ogUrl || "http://ayanshtechnology.com/ai-automation"}
        />
        <link
          rel="canonical"
          href={meta.canonical || "http://ayanshtechnology.com/ai-automation"}
        />
      </Helmet>
      <div className="flex flex-col items-center justify-center mb-6 p-6 mt-5 lg:mt-[120px] md:mt-10">
        <section className="w-full bg-gradient-to-br from-green-50 via-white to-blue-50 rounded-3xl p-10 sm:p-14 mb-12">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-3/4 lg:text-left p-4">
              <p className="text-sm uppercase tracking-[0.2em] text-green-600 font-semibold mb-3">
                AI Automation Services
              </p>
              <h1 className="sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {title}
              </h1>
              <p className="sm:text-lg md:text-xl text-gray-700 mb-6">
                {heroDescription}
              </p>
              <button
                onClick={scrollToContact}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-3 px-6 rounded-full mt-4 transition duration-300"
              >
                {ctaLabel}
              </button>
            </div>
            <div className="flex justify-center mt-10 lg:mt-0">
              <div className="w-full h-full max-w-md rounded-3xl bg-white shadow-2xl p-8 border border-green-100">
                <p className="text-sm text-gray-500 font-semibold mb-4">
                  What you get
                </p>
                <ul className="space-y-3 text-gray-800 text-lg">
                  {features.slice(0, 3).map((feature) => (
                    <li key={feature.title} className="flex items-start gap-3">
                      <span className="text-green-600 mt-1">{feature.icon}</span>
                      <span>{feature.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* <section className="mb-12 w-full">
          <SectionHeading
            title={about.heading}
            subtitle={about.description}
          />
        </section> */}

        <section className="w-full mb-12">
          <SectionHeading
            title="Key AI Features & Capabilities"
            subtitle="Purpose-built accelerators, models, and integrations for intelligent automation."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <FeatureCard key={feature.title} feature={feature} />
            ))}
          </div>
        </section>

        <section className="w-full p-4 mb-12">
          <SectionHeading
            title="How It Works"
            subtitle="A proven delivery playbook from strategy through scale."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {steps.map((step, index) => (
              <StepCard key={step.title} step={step} index={index} />
            ))}
          </div>
        </section>

        <section className="w-full p-4 mb-12">
          <SectionHeading title="Benefits for Businesses" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-start space-x-4 rounded-2xl p-5 shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:border-none "
              >
                <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </section>

        {/* <section className="w-full mb-12">
          <SectionHeading title="Our Expertise" />
          <div className="  p-8">
            <div className="flex flex-col md:flex-row justify-center item-center">
            <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-5xl my-auto space-y-3 text-gray-700">
              {expertise.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="w-full md:w-1/2 lg:w-[40%]">
              <img src="https://spaceliftio.wpcomstaging.com/wp-content/uploads/2025/05/ansible-architecture.png" alt="" />
            </div>
            </div>
          </div>
        </section>
         */}
         <section className="w-full mb-12">
          <SectionHeading title="Our Expertise" />
          <div className="  p-8">
            <div className="flex flex-col md:flex-row justify-around item-center">
            <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-5xl my-auto space-y-3 text-gray-700">
              {expertise.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="w-full md:w-1/2 lg:w-[35%]">
             <img src={image[0].side_image} alt="side_image" />
            </div>
            </div>
          </div>
        </section>

        <section className="w-full p-5 mb-12">
          <SectionHeading title="Real Use Cases" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {useCases.map((useCase) => (
              <UseCaseCard key={useCase.title} useCase={useCase} />
            ))}
          </div>
        </section>

        <section className="w-full mb-12">
          <SectionHeading title="Why Choose Us" />
          <div className=" rounded-3xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reasons.map((reason) => (
                <div
                  key={reason}
                  className="flex items-start space-x-4  rounded-2xl p-4 shadow-md  border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:border-none hover:shadow-none "
                >
                  <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                  <p className="text-lg text-gray-700">{reason}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <button
                onClick={scrollToContact}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
              >
                {ctaLabel}
              </button>
            </div>
          </div>
        </section>

        <div id="contact-section" className="w-full mt-12">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default AIAutomationTemplate;


